// biome-ignore-all lint/style/noParameterAssign: stable
// biome-ignore-all lint/suspicious/noAssignInExpressions: stable
import * as React from "react"
import { flushSync } from "react-dom"
import type { NoInfer, PartialKeys, RemoveUndefined } from "../../shared/types"

export function useVirtualizer<
  scrollElement extends Element,
  itemElement extends Element,
>(
  options: PartialKeys<
    VirtualizerOptions<scrollElement, itemElement>,
    "observeElementRect" | "observeElementOffset" | "scrollToFn"
  >,
): Virtualizer<scrollElement, itemElement> {
  return useVirtualizerBase<scrollElement, itemElement>({
    observeElementRect<element extends Element>(
      instance: Virtualizer<element, any>,
      cb: (rect: Rect) => void,
    ) {
      const element = instance.scrollElement
      if (!element) return
      const targetWindow = instance.targetWindow
      if (!targetWindow) return

      function handler(rect: Rect) {
        const { width, height } = rect
        cb({ width: Math.round(width), height: Math.round(height) })
      }
      handler(getRect(element as unknown as HTMLElement))

      if (!targetWindow.ResizeObserver) return () => {}

      const observer = new targetWindow.ResizeObserver((entries) => {
        function run() {
          const entry = entries[0]
          if (entry?.borderBoxSize) {
            const box = entry.borderBoxSize[0]
            if (box) {
              handler({ width: box.inlineSize, height: box.blockSize })
              return
            }
          }
          handler(getRect(element as unknown as HTMLElement))
        }

        instance.options.useAnimationFrameWithResizeObserver
          ? requestAnimationFrame(run)
          : run()
      })

      observer.observe(element, { box: "border-box" })

      return () => {
        observer.unobserve(element)
      }
    },
    observeElementOffset<element extends Element>(
      instance: Virtualizer<element, any>,
      cb: ObserveOffsetCallBack,
    ) {
      const element = instance.scrollElement
      if (!element) return
      const targetWindow = instance.targetWindow
      if (!targetWindow) return

      let offset = 0
      const fallback =
        instance.options.useScrollendEvent && supportsScrollend
          ? () => undefined
          : debounce(
              targetWindow,
              () => cb(offset, false),
              instance.options.isScrollingResetDelay,
            )

      const createHandler = (isScrolling: boolean) => () => {
        offset = options.reverse
          ? Math.abs(element.scrollTop)
          : element.scrollTop
        fallback()
        cb(offset, isScrolling)
      }
      const handler = createHandler(true)
      const endHandler = createHandler(false)
      endHandler()

      element.addEventListener("scroll", handler, { passive: true })
      const registerScrollendEvent =
        instance.options.useScrollendEvent && supportsScrollend
      if (registerScrollendEvent)
        element.addEventListener("scrollend", endHandler, { passive: true })
      return () => {
        element.removeEventListener("scroll", handler)
        if (registerScrollendEvent)
          element.removeEventListener("scrollend", endHandler)
      }
    },
    scrollToFn<element extends Element>(
      offset: number,
      opts: { adjustments?: number; behavior?: ScrollBehavior },
      instance: Virtualizer<element, any>,
    ) {
      const toOffset = offset + (opts.adjustments ?? 0)

      // In reverse mode, scrollTop is negative, so we need to negate our calculated offset
      const scrollTop = instance.options.reverse ? -toOffset : toOffset

      instance.scrollElement?.scrollTo?.({ ...opts, top: scrollTop })
    },
    ...options,
  })
}

function useVirtualizerBase<
  scrollElement extends Element | Window,
  itemElement extends Element,
>(
  options: VirtualizerOptions<scrollElement, itemElement>,
): Virtualizer<scrollElement, itemElement> {
  const rerender = React.useReducer(() => ({}), {})[1]

  const resolvedOptions: VirtualizerOptions<scrollElement, itemElement> = {
    ...options,
    onChange(instance, sync) {
      if (sync) flushSync(rerender)
      else rerender()
      options.onChange?.(instance, sync)
    },
  }

  const [instance] = React.useState(
    () => new Virtualizer<scrollElement, itemElement>(resolvedOptions),
  )

  instance.setOptions(resolvedOptions)

  useIsomorphicLayoutEffect(() => {
    return instance._didMount()
  }, [])

  useIsomorphicLayoutEffect(() => {
    return instance._willUpdate()
  })

  return instance
}

const useIsomorphicLayoutEffect =
  typeof document !== "undefined" ? React.useLayoutEffect : React.useEffect

const supportsScrollend =
  typeof window === "undefined" ? true : "onscrollend" in window

function getRect(element: HTMLElement): Rect {
  const { offsetWidth, offsetHeight } = element
  return { width: offsetWidth, height: offsetHeight }
}

function debounce(
  targetWindow: Window & typeof globalThis,
  fn: (...args: unknown[]) => void,
  ms: number,
) {
  let timeoutId: number
  return function (this: unknown, ...args: Array<unknown>) {
    targetWindow.clearTimeout(timeoutId)
    timeoutId = targetWindow.setTimeout(() => fn.apply(this, args), ms)
  }
}

export interface VirtualizerOptions<
  getScrollElement extends Element | Window,
  itemElement extends Element,
> {
  // Required from the user
  count: number
  getScrollElement: () => getScrollElement | null
  estimateSize: (index: number) => number

  // Required from the framework adapter (but can be overridden)
  scrollToFn: (
    offset: number,
    options: { adjustments?: number; behavior?: ScrollBehavior },
    instance: Virtualizer<getScrollElement, itemElement>,
  ) => void
  observeElementRect: (
    instance: Virtualizer<getScrollElement, itemElement>,
    cb: (rect: Rect) => void,
    // biome-ignore lint/suspicious/noConfusingVoidType: pass
  ) => void | (() => void)
  observeElementOffset: (
    instance: Virtualizer<getScrollElement, itemElement>,
    cb: ObserveOffsetCallBack,
    // biome-ignore lint/suspicious/noConfusingVoidType: pass
  ) => void | (() => void)

  // Optional
  debug?: boolean
  enabled?: boolean
  gap?: number
  getItemKey?: (index: number) => Key
  indexAttribute?: string
  initialMeasurementsCache?: Array<VirtualItem>
  initialOffset?: number | (() => number)
  initialRect?: Rect
  isScrollingResetDelay?: number
  measureElement?: (
    element: itemElement,
    entry: ResizeObserverEntry | undefined,
    instance: Virtualizer<getScrollElement, itemElement>,
  ) => number
  onChange?: (
    instance: Virtualizer<getScrollElement, itemElement>,
    sync: boolean,
  ) => void
  overscan?: number | undefined
  paddingEnd?: number
  paddingStart?: number
  rangeExtractor?: ((range: Range) => Array<number>) | undefined
  reverse?: boolean | undefined
  scrollMargin?: number
  scrollPaddingEnd?: number
  scrollPaddingStart?: number
  useAnimationFrameWithResizeObserver?: boolean
  useScrollendEvent?: boolean

  // Group support (all optional, all-or-nothing)
  getGroupIdForIndex?: (index: number) => Key | undefined
  getGroupHeaderHeight?: (groupId: Key) => number
}

type GroupFields = "getGroupIdForIndex" | "getGroupHeaderHeight"

// TODO: Add isGroupsEnabled() helper for better type safety when working with group options
export class Virtualizer<
  scrollElement extends Element | Window,
  itemElement extends Element,
> {
  elementsCache = new Map<Key, itemElement>()
  isScrolling = false
  measurementsCache: Array<VirtualItem> = []
  options!: RemoveUndefined<
    Required<Omit<VirtualizerOptions<scrollElement, itemElement>, GroupFields>>
  > &
    Pick<VirtualizerOptions<scrollElement, itemElement>, GroupFields>
  scrollDirection: ScrollDirection | null = null
  scrollElement: scrollElement | null = null
  scrollOffset: number | null = null
  scrollRect: Rect | null = null
  shouldAdjustScrollPositionOnItemSizeChange:
    | ((
        item: VirtualItem,
        delta: number,
        instance: Virtualizer<scrollElement, itemElement>,
      ) => boolean)
    | undefined
  targetWindow: (Window & typeof globalThis) | null = null

  private itemSizeCache = new Map<Key, number>()
  private pendingMeasuredCacheIndexes: Array<number> = []
  private scrollAdjustments = 0
  // biome-ignore lint/suspicious/noConfusingVoidType: pass
  private unsubs: Array<void | (() => void)> = []

  private observer = (() => {
    let _ro: ResizeObserver | null = null

    const get = () => {
      if (_ro) return _ro
      if (!this.targetWindow || !this.targetWindow.ResizeObserver) return null
      return (_ro = new this.targetWindow.ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const run = () => {
            this._measureElement(entry.target as itemElement, entry)
          }
          this.options.useAnimationFrameWithResizeObserver
            ? requestAnimationFrame(run)
            : run()
        })
      }))
    }

    return {
      disconnect: () => {
        get()?.disconnect()
        _ro = null
      },
      observe: (target: Element) =>
        get()?.observe(target, { box: "border-box" }),
      unobserve: (target: Element) => get()?.unobserve(target),
    }
  })()
  range: { startIndex: number; endIndex: number } | null = null

  constructor(opts: VirtualizerOptions<scrollElement, itemElement>) {
    this.setOptions(opts)
  }

  setOptions = (opts: VirtualizerOptions<scrollElement, itemElement>) => {
    Object.entries(opts).forEach(([key, value]) => {
      if (typeof value === "undefined") delete opts[key as keyof typeof opts]
    })

    this.options = {
      debug: false,
      enabled: true,
      gap: 0,
      getItemKey: (index: number) => index,
      indexAttribute: "data-index",
      initialMeasurementsCache: [],
      initialOffset: 0,
      initialRect: { width: 0, height: 0 },
      isScrollingResetDelay: 150,
      measureElement,
      onChange: () => {},
      paddingEnd: 0,
      paddingStart: 0,
      scrollMargin: 0,
      scrollPaddingEnd: 0,
      scrollPaddingStart: 0,
      useAnimationFrameWithResizeObserver: false,
      useScrollendEvent: false,
      ...opts,
      overscan: opts.overscan ?? 1,
      rangeExtractor:
        opts.rangeExtractor ??
        ((range: Range) => {
          const start = Math.max(range.startIndex - range.overscan, 0)
          const end = Math.min(range.endIndex + range.overscan, range.count - 1)
          const arr = []
          for (let i = start; i <= end; i++) arr.push(i)
          return arr
        }),
      reverse: opts.reverse ?? false,
    }
  }

  private notify = (sync: boolean) => {
    this.options.onChange?.(this, sync)
  }

  private maybeNotify = memo(
    () => {
      this.calculateRange()

      return [
        this.isScrolling,
        this.range ? this.range.startIndex : null,
        this.range ? this.range.endIndex : null,
      ]
    },
    (isScrolling) => {
      this.notify(isScrolling)
    },
    {
      key: process.env.NODE_ENV !== "production" && "maybeNotify",
      debug: () => this.options.debug,
      initialDeps: [
        this.isScrolling,
        this.range ? this.range.startIndex : null,
        this.range ? this.range.endIndex : null,
      ] as [boolean, number | null, number | null],
    },
  )

  private cleanup = () => {
    for (const unsub of this.unsubs) {
      if (!unsub) continue
      unsub()
    }
    this.unsubs = []
    this.observer.disconnect()
    this.scrollElement = null
    this.targetWindow = null
  }

  _didMount = () => {
    return () => {
      this.cleanup()
    }
  }

  _willUpdate = () => {
    const scrollElement = this.options.enabled
      ? this.options.getScrollElement()
      : null

    if (this.scrollElement !== scrollElement) {
      this.cleanup()

      if (!scrollElement) {
        this.maybeNotify()
        return
      }

      this.scrollElement = scrollElement

      if (this.scrollElement && "ownerDocument" in this.scrollElement)
        this.targetWindow = this.scrollElement.ownerDocument.defaultView
      else this.targetWindow = this.scrollElement?.window ?? null

      this.elementsCache.forEach((cached) => {
        this.observer.observe(cached)
      })

      this._scrollToOffset(this.getScrollOffset(), {
        adjustments: undefined,
        behavior: undefined,
      })

      this.unsubs.push(
        this.options.observeElementRect(this, (rect) => {
          this.scrollRect = rect
          this.maybeNotify()
        }),
      )

      this.unsubs.push(
        this.options.observeElementOffset(this, (offset, isScrolling) => {
          this.scrollAdjustments = 0
          this.scrollDirection = isScrolling
            ? (
                this.options.reverse
                  ? this.getScrollOffset() > offset
                  : this.getScrollOffset() < offset
              )
              ? "forward"
              : "backward"
            : null
          this.scrollOffset = offset
          this.isScrolling = isScrolling

          this.maybeNotify()
        }),
      )
    }
  }

  private getSize = () => {
    if (!this.options.enabled) {
      this.scrollRect = null
      return 0
    }
    this.scrollRect = this.scrollRect ?? this.options.initialRect
    return this.scrollRect.height
  }

  private getScrollOffset() {
    if (!this.options.enabled) {
      this.scrollOffset = null
      return 0
    }

    this.scrollOffset =
      this.scrollOffset ??
      (typeof this.options.initialOffset === "function"
        ? this.options.initialOffset()
        : this.options.initialOffset)

    // With column-reverse, scrollTop can be negative
    // scrollTop = 0: at bottom (item 0)
    // scrollTop < 0: scrolled up (higher items)
    // We need absolute value to get the actual scroll distance
    if (this.options.reverse) return Math.abs(this.scrollOffset)

    return this.scrollOffset
  }

  private getMeasurementOptions = memo(
    () => [
      this.options.count,
      this.options.paddingStart,
      this.options.scrollMargin,
      this.options.getItemKey,
      this.options.enabled,
    ],
    (count, paddingStart, scrollMargin, getItemKey, enabled) => {
      this.pendingMeasuredCacheIndexes = []
      return {
        count,
        paddingStart,
        scrollMargin,
        getItemKey,
        enabled,
      }
    },
    {
      key: false,
    },
  )

  private getMeasurements = memo(
    () => [this.getMeasurementOptions(), this.itemSizeCache],
    (opts, itemSizeCache) => {
      if (!opts.enabled) {
        this.measurementsCache = []
        this.itemSizeCache.clear()
        return []
      }

      if (this.measurementsCache.length === 0) {
        this.measurementsCache = this.options.initialMeasurementsCache
        this.measurementsCache.forEach((item) => {
          this.itemSizeCache.set(item.key, item.size)
        })
      }

      const min =
        this.pendingMeasuredCacheIndexes.length > 0
          ? Math.min(...this.pendingMeasuredCacheIndexes)
          : 0
      this.pendingMeasuredCacheIndexes = []

      const measurements = this.measurementsCache.slice(0, min)

      for (let i = min; i < opts.count; i++) {
        const key = opts.getItemKey(i)

        const previousMeasurement = measurements[i - 1]

        let start = previousMeasurement
          ? previousMeasurement.end + this.options.gap
          : opts.paddingStart + opts.scrollMargin

        // Check for group header
        if (this.options.getGroupIdForIndex) {
          const groupId = this.options.getGroupIdForIndex(i)
          if (groupId) {
            const prevGroupId =
              i > 0 ? this.options.getGroupIdForIndex(i - 1) : null
            if (groupId !== prevGroupId) {
              // First item in new group - add header height
              if (!this.options.getGroupHeaderHeight) {
                if (process.env.NODE_ENV !== "production")
                  console.warn(
                    "getGroupHeaderHeight is required when using groups but was not provided.",
                  )
              } else {
                const headerHeight = this.options.getGroupHeaderHeight(groupId)
                start += headerHeight

                if (process.env.NODE_ENV !== "production" && this.options.debug)
                  console.info("Group header added", groupId, i)
              }
            }
          }
        }

        const measuredSize = itemSizeCache.get(key)
        const size =
          typeof measuredSize === "number"
            ? measuredSize
            : this.options.estimateSize(i)

        const end = start + size

        measurements[i] = {
          index: i,
          start,
          size,
          end,
          key,
        }
      }

      this.measurementsCache = measurements

      return measurements
    },
    {
      key: process.env.NODE_ENV !== "production" && "getMeasurements",
      debug: () => this.options.debug,
    },
  )

  calculateRange = memo(
    () => [this.getMeasurements(), this.getSize(), this.getScrollOffset()],
    (measurements, outerSize, scrollOffset) => {
      this.range =
        measurements.length > 0 && outerSize > 0
          ? calculateRange({
              measurements,
              outerSize,
              scrollOffset,
            })
          : null
      return this.range
    },
    {
      key: process.env.NODE_ENV !== "production" && "calculateRange",
      debug: () => this.options.debug,
    },
  )

  getVirtualIndexes = memo(
    () => {
      let startIndex: number | null = null
      let endIndex: number | null = null
      const range = this.calculateRange()
      if (range) {
        startIndex = range.startIndex
        endIndex = range.endIndex
      }
      this.maybeNotify.updateDeps([this.isScrolling, startIndex, endIndex])
      return [
        this.options.rangeExtractor,
        this.options.overscan,
        this.options.count,
        startIndex,
        endIndex,
      ]
    },
    (rangeExtractor, overscan, count, startIndex, endIndex) => {
      return startIndex === null || endIndex === null
        ? []
        : rangeExtractor({
            startIndex,
            endIndex,
            overscan,
            count,
          })
    },
    {
      key: process.env.NODE_ENV !== "production" && "getVirtualIndexes",
      debug: () => this.options.debug,
    },
  )

  indexFromElement = (node: itemElement) => {
    const attributeName = this.options.indexAttribute
    const indexStr = node.getAttribute(attributeName)

    if (!indexStr) {
      console.warn(
        `Missing attribute name '${attributeName}={index}' on measured element.`,
      )
      return -1
    }

    return Number.parseInt(indexStr, 10)
  }

  private _measureElement = (
    node: itemElement,
    entry: ResizeObserverEntry | undefined,
  ) => {
    const index = this.indexFromElement(node)
    const item = this.measurementsCache[index]
    if (!item) return
    const key = item.key
    const prevNode = this.elementsCache.get(key)

    if (prevNode !== node) {
      if (prevNode) this.observer.unobserve(prevNode)
      this.observer.observe(node)
      this.elementsCache.set(key, node)
    }

    if (node.isConnected)
      this.resizeItem(index, this.options.measureElement(node, entry, this))
  }

  resizeItem = (index: number, size: number) => {
    const item = this.measurementsCache[index]
    if (!item) return

    const itemSize = this.itemSizeCache.get(item.key) ?? item.size
    const delta = size - itemSize

    if (delta !== 0) {
      if (
        this.shouldAdjustScrollPositionOnItemSizeChange !== undefined
          ? this.shouldAdjustScrollPositionOnItemSizeChange(item, delta, this)
          : item.start < this.getScrollOffset() + this.scrollAdjustments
      ) {
        if (process.env.NODE_ENV !== "production" && this.options.debug)
          console.info("correction", delta)

        this._scrollToOffset(this.getScrollOffset(), {
          adjustments: (this.scrollAdjustments += delta),
          behavior: undefined,
        })
      }

      this.pendingMeasuredCacheIndexes.push(item.index)
      this.itemSizeCache = new Map(this.itemSizeCache.set(item.key, size))

      this.notify(false)
    }
  }

  measureElement = (node: itemElement | null | undefined) => {
    if (!node) {
      this.elementsCache.forEach((cached, key) => {
        if (!cached.isConnected) this.observer.unobserve(cached)
        this.elementsCache.delete(key)
      })
      return
    }

    this._measureElement(node, undefined)
  }

  getVirtualItems = memo(
    () => [this.getVirtualIndexes(), this.getMeasurements()],
    (indexes, measurements) => {
      const virtualItems: Array<VirtualItem> = []
      for (let k = 0, len = indexes.length; k < len; k++) {
        const i = indexes[k]!
        const measurement = measurements[i]!
        const groupId = this.options.getGroupIdForIndex?.(i)
        virtualItems.push({ ...measurement, ...(groupId && { groupId }) })
      }
      return virtualItems
    },
    {
      key: process.env.NODE_ENV !== "production" && "getVirtualItems",
      debug: () => this.options.debug,
    },
  )

  getVirtualGroups = memo(
    () => [this.getVirtualItems()],
    (items) => {
      if (!this.options.getGroupIdForIndex) return []

      const groupMap = new Map<Key, VirtualGroup>()
      for (let k = 0, len = items.length; k < len; k++) {
        const item = items[k]!
        if (!item.groupId) continue
        if (!groupMap.has(item.groupId))
          groupMap.set(item.groupId, {
            id: item.groupId,
            headerHeight: this.options.getGroupHeaderHeight!(item.groupId),
            items: [item],
          })
        else groupMap.get(item.groupId)!.items.push(item)
      }

      return Array.from(groupMap.values()).toSorted(
        (a, b) => a.items[0]!.index - b.items[0]!.index,
      )
    },
    {
      key: process.env.NODE_ENV !== "production" && "getVirtualGroups",
      debug: () => this.options.debug,
    },
  )

  getVirtualItemForOffset = (offset: number) => {
    const measurements = this.getMeasurements()
    if (measurements.length === 0) return undefined

    return notUndefined(
      measurements[
        findNearestBinarySearch(
          0,
          measurements.length - 1,
          (index: number) => notUndefined(measurements[index]).start,
          offset,
        )
      ],
    )
  }

  getOffsetForAlignment = (
    toOffset: number,
    align: ScrollAlignment,
    itemSize = 0,
  ) => {
    const size = this.getSize()
    const scrollOffset = this.getScrollOffset()

    if (align === "auto")
      align = toOffset >= scrollOffset + size ? "end" : "start"

    if (align === "center")
      // When aligning to a particular item (e.g. with scrollToIndex),
      // adjust offset by the size of the item to center on the item
      toOffset += (itemSize - size) / 2
    else if (align === "end") toOffset -= size

    const maxOffset = this.getTotalSize() + this.options.scrollMargin - size

    return Math.max(Math.min(maxOffset, toOffset), 0)
  }

  getOffsetForIndex = (index: number, align: ScrollAlignment = "auto") => {
    index = Math.max(0, Math.min(index, this.options.count - 1))

    const item = this.measurementsCache[index]
    if (!item) return undefined

    const size = this.getSize()
    const scrollOffset = this.getScrollOffset()

    if (align === "auto") {
      if (item.end >= scrollOffset + size - this.options.scrollPaddingEnd)
        align = "end"
      else if (item.start <= scrollOffset + this.options.scrollPaddingStart)
        align = "start"
      else return [scrollOffset, align] as const
    }

    const toOffset =
      align === "end"
        ? item.end + this.options.scrollPaddingEnd
        : item.start - this.options.scrollPaddingStart

    return [
      this.getOffsetForAlignment(toOffset, align, item.size),
      align,
    ] as const
  }

  private isDynamicMode = () => this.elementsCache.size > 0

  scrollToOffset = (toOffset: number, opts: ScrollToOptions = {}) => {
    if (opts.behavior === "smooth" && this.isDynamicMode())
      console.warn(
        "The `smooth` scroll behavior is not fully supported with dynamic size.",
      )

    this._scrollToOffset(
      this.getOffsetForAlignment(toOffset, opts.align ?? "start"),
      {
        adjustments: undefined,
        behavior: opts.behavior,
      },
    )
  }

  scrollToIndex = (index: number, opts: ScrollToOptions = {}) => {
    if (opts.behavior === "smooth" && this.isDynamicMode())
      console.warn(
        "The `smooth` scroll behavior is not fully supported with dynamic size.",
      )

    index = Math.max(0, Math.min(index, this.options.count - 1))

    let attempts = 0
    const maxAttempts = 10

    const tryScroll = (currentAlign: ScrollAlignment) => {
      if (!this.targetWindow) return

      const offsetInfo = this.getOffsetForIndex(index, currentAlign)
      if (!offsetInfo) {
        console.warn("Failed to get offset for index:", index)
        return
      }
      const [offset, align] = offsetInfo
      this._scrollToOffset(offset, {
        adjustments: undefined,
        behavior: opts.behavior,
      })

      this.targetWindow.requestAnimationFrame(() => {
        const currentOffset = this.getScrollOffset()
        const afterInfo = this.getOffsetForIndex(index, align)
        if (!afterInfo) {
          console.warn("Failed to get offset for index:", index)
          return
        }

        if (!approxEqual(afterInfo[0], currentOffset)) scheduleRetry(align)
      })
    }

    const scheduleRetry = (align: ScrollAlignment) => {
      if (!this.targetWindow) return

      attempts++
      if (attempts < maxAttempts) {
        if (process.env.NODE_ENV !== "production" && this.options.debug)
          console.info("Schedule retry", attempts, maxAttempts)
        this.targetWindow.requestAnimationFrame(() => tryScroll(align))
      } else
        console.warn(
          `Failed to scroll to index ${index} after ${maxAttempts} attempts.`,
        )
    }

    tryScroll(opts.align ?? "auto")
  }

  scrollBy = (delta: number, opts: ScrollToOptions = {}) => {
    if (opts.behavior === "smooth" && this.isDynamicMode())
      console.warn(
        "The `smooth` scroll behavior is not fully supported with dynamic size.",
      )

    this._scrollToOffset(this.getScrollOffset() + delta, {
      adjustments: undefined,
      behavior: opts.behavior,
    })
  }

  getTotalSize = () => {
    const measurements = this.getMeasurements()

    const end =
      measurements.length === 0
        ? this.options.paddingStart
        : (measurements[measurements.length - 1]?.end ?? 0)

    return Math.max(
      end - this.options.scrollMargin + this.options.paddingEnd,
      0,
    )
  }

  getStartSpacerSize = () => {
    const items = this.getVirtualItems()
    if (items.length === 0) return 0

    const firstItem = items[0]!

    // If groups are enabled, subtract the header height of the first visible group
    // because the header is rendered as part of the visible content, not the spacer
    if (this.options.getGroupIdForIndex && this.options.getGroupHeaderHeight) {
      const groupId = firstItem.groupId!
      const headerHeight = this.options.getGroupHeaderHeight(groupId)
      return firstItem.start - headerHeight
    }

    return firstItem.start
  }

  getEndSpacerSize = () => {
    const items = this.getVirtualItems()
    if (items.length === 0) return 0
    const lastItem = items[items.length - 1]!
    return this.getTotalSize() - lastItem.end
  }

  private _scrollToOffset = (
    offset: number,
    opts: {
      adjustments: number | undefined
      behavior: ScrollBehavior | undefined
    },
  ) => {
    this.options.scrollToFn(
      offset,
      {
        ...(opts.adjustments && { adjustments: opts.adjustments }),
        ...(opts.behavior && { behavior: opts.behavior }),
      },
      this,
    )
  }

  measure() {
    this.itemSizeCache = new Map()
    this.notify(false)
  }
}

const findNearestBinarySearch = (
  low: number,
  high: number,
  getCurrentValue: (i: number) => number,
  value: number,
) => {
  while (low <= high) {
    const middle = ((low + high) / 2) | 0
    const currentValue = getCurrentValue(middle)

    if (currentValue < value) low = middle + 1
    else if (currentValue > value) high = middle - 1
    else return middle
  }

  if (low > 0) return low - 1
  return 0
}

function calculateRange(opts: {
  measurements: Array<VirtualItem>
  outerSize: number
  scrollOffset: number
}) {
  const lastIndex = opts.measurements.length - 1
  const getOffset = (index: number) => opts.measurements[index]!.start

  const startIndex = findNearestBinarySearch(
    0,
    lastIndex,
    getOffset,
    opts.scrollOffset,
  )
  let endIndex = startIndex

  while (
    endIndex < lastIndex &&
    opts.measurements[endIndex]!.end < opts.scrollOffset + opts.outerSize
  )
    endIndex++

  return { startIndex, endIndex }
}

type ScrollDirection = "forward" | "backward"
type ScrollAlignment = "start" | "center" | "end" | "auto"
type ScrollBehavior = "auto" | "smooth"

interface ScrollToOptions {
  align?: ScrollAlignment
  behavior?: ScrollBehavior
}

export interface Range {
  startIndex: number
  endIndex: number
  overscan: number
  count: number
}

type Key = number | string | bigint

export interface VirtualItem {
  end: number
  index: number
  key: Key
  size: number
  start: number
  groupId?: Key
}

export interface VirtualGroup {
  id: Key
  headerHeight: number
  items: Array<VirtualItem>
}

interface Rect {
  height: number
  width: number
}

type ObserveOffsetCallBack = (offset: number, isScrolling: boolean) => void

function measureElement<itemElement extends Element>(
  element: itemElement,
  entry: ResizeObserverEntry | undefined,
  _instance: Virtualizer<any, itemElement>,
) {
  if (entry?.borderBoxSize) {
    const box = entry.borderBoxSize[0]
    if (box) {
      const size = Math.round(box.blockSize)
      return size
    }
  }

  return (element as unknown as HTMLElement).offsetHeight
}

export function memo<deps extends ReadonlyArray<any>, result>(
  getDeps: () => [...deps],
  fn: (...args: NoInfer<[...deps]>) => result,
  opts: {
    key: false | string
    debug?: () => boolean
    onChange?: (result: result) => void
    initialDeps?: deps
  },
) {
  let deps = opts.initialDeps ?? []
  let result: result | undefined

  function memoizedFunction(): result {
    let depTime: number
    if (opts.key && opts.debug?.()) depTime = Date.now()

    const newDeps = getDeps()

    const depsChanged =
      newDeps.length !== deps.length ||
      newDeps.some((dep: any, index: number) => deps[index] !== dep)

    if (!depsChanged) return result!

    deps = newDeps

    let resultTime: number
    if (opts.key && opts.debug?.()) resultTime = Date.now()

    result = fn(...newDeps)

    if (opts.key && opts.debug?.()) {
      const depEndTime = Math.round((Date.now() - depTime!) * 100) / 100
      const resultEndTime = Math.round((Date.now() - resultTime!) * 100) / 100
      const resultFpsPercentage = resultEndTime / 16

      const pad = (str: number | string, num: number) => {
        str = String(str)
        while (str.length < num) str = ` ${str}`
        return str
      }

      console.info(
        `%c⏱ ${pad(resultEndTime, 5)} /${pad(depEndTime, 5)} ms`,
        `
            font-size: .6rem;
            font-weight: bold;
            color: hsl(${Math.max(
              0,
              Math.min(120 - 120 * resultFpsPercentage, 120),
            )}deg 100% 31%);`,
        opts?.key,
      )
    }

    opts?.onChange?.(result)

    return result
  }

  // Attach updateDeps to the function itself
  memoizedFunction.updateDeps = (newDeps: [...deps]) => {
    deps = newDeps
  }

  return memoizedFunction
}

function notUndefined<T>(value: T | undefined, msg?: string): T {
  if (value === undefined)
    throw new Error(`Unexpected undefined${msg ? `: ${msg}` : ""}`)
  return value
}

function approxEqual(a: number, b: number) {
  return Math.abs(a - b) < 1.01
}
